import * as tslib_1 from "tslib";
import { AfterViewInit, ElementRef, EventEmitter, NgZone, OnChanges, OnDestroy, QueryList, SimpleChanges } from '@angular/core';
import { AgmInfoWindow, AgmMap, AgmMarker } from '@agm/core';
import { AgmSnazzyInfoWindow } from '@agm/snazzy-info-window';
import { ControlPosition } from '@agm/core/services/google-maps-types';
import { GoogleService } from '../../google/google.service';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { LatLon } from '../../util/geodesy/latlon';
import { AviationEvent, AviationEventType } from '../../shared/models/aviation-event.model';
import { FlyingClubResult } from '../../shared/models/flying-club-result.model';
import { FlightSchoolResult } from '../../shared/models/flight-school-result.model';
import { UniversityResult } from '../../shared/models/university-result.model';
import { FboResult } from '../../shared/models/fbo-result.model';
import { ServiceRepairResult } from '../../shared/models/service-repair-result.model';
import { RentalSalesResult } from '../../shared/models/rental-sales-result.model';
import { AviationEventFilter } from '../../shared/models/aviation-event-filter.model';
import * as moment from 'moment-timezone';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';
// The business-finder markers (Flying Clubs or Flight Schools — only one tab is ever active) and their
// clustering are self-managed (see recluster()): plain google.maps.Marker bubbles that remove reliably via
// setMap(null). The old js-marker-clusterer leaked orphaned OverlayView cluster-icon DOM that nothing could
// clear (ghost "3" bubble showing for a 1-club search), so it was abandoned.
/**
 * Inline SVG data URI for a blue cluster bubble circle of the given px diameter (no external image needed).
 * base64-encoded so it is safe as a marker icon url. The count is drawn via the marker's label, not the SVG.
 */
function blueClusterBubble(size) {
    var c = size / 2;
    var svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"" + size + "\" height=\"" + size + "\">" +
        ("<circle cx=\"" + c + "\" cy=\"" + c + "\" r=\"" + c + "\" fill=\"#1e88e5\" fill-opacity=\"0.3\"/>") +
        ("<circle cx=\"" + c + "\" cy=\"" + c + "\" r=\"" + size * 0.36 + "\" fill=\"#1e88e5\"/>") +
        "</svg>";
    return 'data:image/svg+xml;base64,' + btoa(svg);
}
/**
 * Inline SVG data URI for the flight-school map marker: a black (#212121) teardrop pin (evoking a graduation
 * mortar board) with a small white graduation cap. Kept inline (like {@link blueClusterBubble}) so no binary
 * asset is needed and the school pins read distinctly from the flying-club PNG pins.
 */
var FLIGHT_SCHOOL_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#212121"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path d="M13.5 8 4.5 11.5 13.5 15 22.5 11.5 13.5 8z" fill="#212121"/>' +
    '<path d="M8.5 13.2v3.1c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5v-3.1L13.5 15.4 8.5 13.2z" fill="#212121"/>' +
    '<rect x="21.7" y="11.5" width="1.1" height="4.2" fill="#212121"/>' +
    '</svg>');
/**
 * Inline SVG data URI for the university map marker: a purple (#6A1B9A) teardrop pin with a small white
 * classical building (pediment + columns), so university pins read distinctly from the flight-school pins.
 */
var UNIVERSITY_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#6A1B9A"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path d="M13.5 7.3 5 11.2 22 11.2z" fill="#6A1B9A"/>' +
    '<rect x="6.4" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="10" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="13.6" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="17.2" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="5" y="17" width="16.5" height="1.7" fill="#6A1B9A"/>' +
    '</svg>');
/**
 * Inline SVG data URI for the Business Finder (FBO) map marker: a red (#D32F2F) teardrop pin with a small
 * white fuel pump, so FBO pins read distinctly from the other finders' pins.
 */
var FBO_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#D32F2F"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<rect x="8" y="7.6" width="6.4" height="11.8" rx="1.2" fill="#D32F2F"/>' +
    '<rect x="9.3" y="9" width="3.8" height="3" rx="0.4" fill="#ffffff"/>' +
    '<rect x="9.3" y="13.4" width="3.8" height="1.1" fill="#ffffff"/>' +
    '<rect x="16" y="11.2" width="1.4" height="8.2" rx="0.6" fill="#D32F2F"/>' +
    '<rect x="15.2" y="10" width="2.9" height="1.5" rx="0.6" fill="#D32F2F"/>' +
    '</svg>');
/**
 * Inline SVG data URI for the Service/Repair Finder map marker: a bright orange (#FF6D00) teardrop pin with a small
 * white circle carrying a green open-end wrench (the standard spanner glyph, matching the "build" tab icon),
 * so service/repair pins read distinctly from the other finders' pins. The wrench path is the 24x24 Material
 * "wrench" glyph, scaled/translated to sit inside the white circle.
 */
var SERVICE_REPAIR_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#FF6D00"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path transform="translate(5.9 5.9) scale(0.64)" fill="#FF6D00" ' +
    'd="M22.7 19L13.6 9.9C14.5 7.6 14 4.9 12.1 3C10.1 1 7.1 0.6 4.7 1.7L9 6L6 9L1.6 4.7C0.6 7.1 1 10.1 3 12.1C4.9 14 7.6 14.5 9.9 13.6L19 22.7C19.4 23.1 20 23.1 20.4 22.7L22.6 20.5C23.1 20 23.1 19.4 22.7 19Z"/>' +
    '</svg>');
/**
 * Inline SVG data URI for the Rentals/Sales Finder map marker: a deep green (#2E7D32) teardrop pin with a
 * small white circle carrying a green price tag (the Material "local_offer" glyph, matching the tab icon,
 * scaled/translated to sit inside the white circle), so rentals/sales pins read distinctly from the other
 * finders' pins.
 */
var RENTAL_SALES_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#2E7D32"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path transform="translate(5.9 5.9) scale(0.64)" fill="#2E7D32" ' +
    'd="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>' +
    '</svg>');
var ResultsMapComponent = /** @class */ (function () {
    function ResultsMapComponent(_zone, el, _google, _analyticsService, _auth) {
        this._zone = _zone;
        this.el = el;
        this._google = _google;
        this._analyticsService = _analyticsService;
        this._auth = _auth;
        this.onEventsTab = false;
        this.flyingClubs = [];
        this.onFlyingClubsTab = false;
        this.mapFlyingClubs = []; // clubs with coordinates, used for map markers
        this.flightSchools = [];
        this.onFlightSchoolsTab = false;
        this.mapFlightSchools = []; // schools with coordinates, used for map markers
        this.universities = [];
        this.onUniversitiesTab = false;
        this.mapUniversities = []; // universities with coordinates, used for map markers
        this.fbos = [];
        this.onFbosTab = false;
        this.mapFbos = []; // FBOs with coordinates, used for map markers
        this.serviceRepairs = [];
        this.onServiceRepairsTab = false;
        this.mapServiceRepairs = []; // service/repair businesses with coordinates, used for map markers
        this.rentalSales = [];
        this.onRentalSalesTab = false;
        this.mapRentalSales = []; // rentals/sales businesses with coordinates, used for map markers
        this.currZoom = 0; // map zoom, bound to <agm-map [zoom]> (mirrors events cluster)
        this.CLUSTER_GRID_PX = 60; // markers within this pixel distance group into one bubble
        this.DECLUSTER_ZOOM = 15; // at/above this zoom every club shows individually (no bubbles)
        this.airportClick = new EventEmitter();
        this.airportMouseEnter = new EventEmitter();
        this.airportMouseLeave = new EventEmitter();
        this.airportCardClose = new EventEmitter();
        this.activityClick = new EventEmitter();
        this.activityMouseEnter = new EventEmitter();
        this.activityMouseLeave = new EventEmitter();
        this.activityCardClose = new EventEmitter();
        this.boundsChange = new EventEmitter();
        this.aviationEventClick = new EventEmitter();
        this.aviationEventMouseEnter = new EventEmitter();
        this.aviationEventMouseLeave = new EventEmitter();
        this.aviationEventCardClose = new EventEmitter();
        this.flyingClubClick = new EventEmitter();
        this.flyingClubMouseEnter = new EventEmitter();
        this.flyingClubMouseLeave = new EventEmitter();
        this.flightSchoolClick = new EventEmitter();
        this.flightSchoolMouseEnter = new EventEmitter();
        this.flightSchoolMouseLeave = new EventEmitter();
        this.universityClick = new EventEmitter();
        this.universityMouseEnter = new EventEmitter();
        this.universityMouseLeave = new EventEmitter();
        this.fboClick = new EventEmitter();
        this.fboMouseEnter = new EventEmitter();
        this.fboMouseLeave = new EventEmitter();
        this.serviceRepairClick = new EventEmitter();
        this.serviceRepairMouseEnter = new EventEmitter();
        this.serviceRepairMouseLeave = new EventEmitter();
        this.rentalSalesClick = new EventEmitter();
        this.rentalSalesMouseEnter = new EventEmitter();
        this.rentalSalesMouseLeave = new EventEmitter();
        this.mapTypeControlOptions = {
            position: ControlPosition.TOP_RIGHT
        };
        this._clubMarkers = []; // one native marker per plottable club (shown when unclustered)
        this._schoolMarkers = []; // one native marker per plottable flight school
        this._universityMarkers = []; // one native marker per plottable university
        this._fboMarkers = []; // one native marker per plottable FBO
        this._serviceRepairMarkers = []; // one native marker per plottable service/repair business
        this._rentalSalesMarkers = []; // one native marker per plottable rentals/sales business
        this._clusterBubbles = []; // count bubbles for the current zoom (rebuilt each re-cluster)
    }
    Object.defineProperty(ResultsMapComponent.prototype, "_finderMarkers", {
        /**
         * The active business-finder marker set to cluster, by tab: universities / flight schools / (default)
         * flying clubs. Only one finder tab is ever active, so the inactive sets are empty and this is unambiguous.
         */
        get: function () {
            if (this.onFlightSchoolsTab) {
                return this._schoolMarkers;
            }
            if (this.onUniversitiesTab) {
                return this._universityMarkers;
            }
            if (this.onFbosTab) {
                return this._fboMarkers;
            }
            if (this.onServiceRepairsTab) {
                return this._serviceRepairMarkers;
            }
            if (this.onRentalSalesTab) {
                return this._rentalSalesMarkers;
            }
            return this._clubMarkers;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ResultsMapComponent.prototype, "identifier", {
        get: function () {
            if (this.activeAirport.identifier != null && this.activeAirport.identifier.toUpperCase().startsWith('ZZ')) {
                return '';
            }
            else {
                return this.activeAirport.identifier + ' :';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ResultsMapComponent.prototype, "eventDatesMessage", {
        get: function () {
            var queryStart = moment(new Date(this.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
            var queryEnd = moment(new Date(this.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));
            var queryStartDateDisplay = moment(queryStart).local().format('MM-DD-YYYY');
            var queryEndDateDisplay = moment(queryEnd).local().format('MM-DD-YYYY');
            return 'Displaying Events From ' + queryStartDateDisplay + ' TO ' + queryEndDateDisplay;
        },
        enumerable: true,
        configurable: true
    });
    ResultsMapComponent.prototype.ngAfterViewInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.map.mapReady.subscribe(function (map) {
                            _this._nativeMap = map;
                            _this._finderInfoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
                            // Re-cluster when the map settles at a new zoom. This raw 'idle' can be unreliable on this map, so
                            // onZoomChange() (AGM's (zoomChange), which DOES fire) also drives scheduleRecluster(); idle also
                            // handles the first clustering once the projection is ready. Both share the debounced rebuild.
                            google.maps.event.addListener(map, 'idle', function () {
                                var zoom = map.getZoom();
                                if (zoom !== _this._lastClusterZoom) {
                                    _this._lastClusterZoom = zoom;
                                    _this.scheduleRecluster();
                                }
                            });
                            _this.renderFlyingClubMarkers();
                            _this.renderFlightSchoolMarkers();
                            _this.renderUniversityMarkers();
                            _this.renderFboMarkers();
                            _this.renderServiceRepairMarkers();
                            _this.renderRentalSalesMarkers();
                        });
                        return [4 /*yield*/, this._google.promise];
                    case 1:
                        _a.sent();
                        // Initialize map bounds
                        this.updateBounds();
                        this._analyticsService.pageView('airport_search_results', 'Airport/Event Search Results');
                        if (this._auth.authenticated && this._auth.endUser) {
                            this._analyticsService.trackEvent('airport_search_results_loggedin', this._auth.recid, this._auth.recid);
                        }
                        else {
                            this._analyticsService.trackEvent('airport_search_results_loggedout', '', '');
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    ResultsMapComponent.prototype.ngOnChanges = function (changes) {
        this.aviationEvents = this.aviationEvents.filter(function (ae) {
            if ((ae.format === 'In-Person' || ae.format === 'Both') &&
                (ae.location && ae.location.lat && ae.location.lon)) {
                return true;
            }
            else {
                return false;
            }
        });
        // Rebuild club markers only when the club set or tab actually changed — not on every input change
        // (e.g. activeFlyingClub on list hover) — so we don't churn/rebuild markers underneath the clusterer.
        if (changes['flyingClubs'] || changes['onFlyingClubsTab']) {
            // Only clubs with coordinates can be plotted; keep indices aligned with the tooltip lookup. Off-tab = none.
            this.mapFlyingClubs = this.onFlyingClubsTab
                ? (this.flyingClubs || []).filter(function (fc) { return fc.location && fc.location.lat && fc.location.lon; })
                : [];
            this.renderFlyingClubMarkers();
        }
        // Same as above for flight schools (only one finder tab is active, so the other set ends up empty).
        if (changes['flightSchools'] || changes['onFlightSchoolsTab']) {
            this.mapFlightSchools = this.onFlightSchoolsTab
                ? (this.flightSchools || []).filter(function (fs) { return fs.location && fs.location.lat && fs.location.lon; })
                : [];
            this.renderFlightSchoolMarkers();
        }
        // Same as above for universities.
        if (changes['universities'] || changes['onUniversitiesTab']) {
            this.mapUniversities = this.onUniversitiesTab
                ? (this.universities || []).filter(function (u) { return u.location && u.location.lat && u.location.lon; })
                : [];
            this.renderUniversityMarkers();
        }
        // Same as above for FBOs (Business Finder).
        if (changes['fbos'] || changes['onFbosTab']) {
            this.mapFbos = this.onFbosTab
                ? (this.fbos || []).filter(function (fbo) { return fbo.location && fbo.location.lat && fbo.location.lon; })
                : [];
            this.renderFboMarkers();
        }
        // Same as above for the Service/Repair Finder.
        if (changes['serviceRepairs'] || changes['onServiceRepairsTab']) {
            this.mapServiceRepairs = this.onServiceRepairsTab
                ? (this.serviceRepairs || []).filter(function (sr) { return sr.location && sr.location.lat && sr.location.lon; })
                : [];
            this.renderServiceRepairMarkers();
        }
        // Same as above for the Rentals/Sales Finder.
        if (changes['rentalSales'] || changes['onRentalSalesTab']) {
            this.mapRentalSales = this.onRentalSalesTab
                ? (this.rentalSales || []).filter(function (rs) { return rs.location && rs.location.lat && rs.location.lon; })
                : [];
            this.renderRentalSalesMarkers();
        }
    };
    /** Rebuilds the per-club native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderFlyingClubMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._clubMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._clubMarkers = this.mapFlyingClubs.map(function (club) {
            var marker = new google.maps.Marker({
                position: { lat: club.location.lat, lng: club.location.lon },
                icon: _this.getFlyingClubIconUrl(club),
                opacity: 0.8,
                title: club.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onFlyingClubMarkerClick(club); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onFlyingClubMarkerHover(club, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onFlyingClubMarkerHover(club, false); }); });
            return marker;
        });
        this.recluster();
    };
    /** Rebuilds the per-school native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderFlightSchoolMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._schoolMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._schoolMarkers = this.mapFlightSchools.map(function (school) {
            var marker = new google.maps.Marker({
                position: { lat: school.location.lat, lng: school.location.lon },
                icon: _this.getFlightSchoolIconUrl(school),
                opacity: 0.8,
                title: school.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onFlightSchoolMarkerClick(school); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onFlightSchoolMarkerHover(school, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onFlightSchoolMarkerHover(school, false); }); });
            return marker;
        });
        this.recluster();
    };
    /** Rebuilds the per-university native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderUniversityMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._universityMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._universityMarkers = this.mapUniversities.map(function (university) {
            var marker = new google.maps.Marker({
                position: { lat: university.location.lat, lng: university.location.lon },
                icon: _this.getUniversityIconUrl(university),
                opacity: 0.8,
                title: university.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onUniversityMarkerClick(university); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onUniversityMarkerHover(university, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onUniversityMarkerHover(university, false); }); });
            return marker;
        });
        this.recluster();
    };
    /** Rebuilds the per-FBO native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderFboMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._fboMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._fboMarkers = this.mapFbos.map(function (fbo) {
            var marker = new google.maps.Marker({
                position: { lat: fbo.location.lat, lng: fbo.location.lon },
                icon: _this.getFboIconUrl(fbo),
                opacity: 0.8,
                title: fbo.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onFboMarkerClick(fbo); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onFboMarkerHover(fbo, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onFboMarkerHover(fbo, false); }); });
            return marker;
        });
        this.recluster();
    };
    /** Rebuilds the per-business Service/Repair native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderServiceRepairMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._serviceRepairMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._serviceRepairMarkers = this.mapServiceRepairs.map(function (sr) {
            var marker = new google.maps.Marker({
                position: { lat: sr.location.lat, lng: sr.location.lon },
                icon: _this.getServiceRepairIconUrl(sr),
                opacity: 0.8,
                title: sr.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onServiceRepairMarkerClick(sr); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onServiceRepairMarkerHover(sr, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onServiceRepairMarkerHover(sr, false); }); });
            return marker;
        });
        this.recluster();
    };
    /** Rebuilds the per-business Rentals/Sales native markers (kept off-map until placed by recluster) then re-clusters them. */
    ResultsMapComponent.prototype.renderRentalSalesMarkers = function () {
        var _this = this;
        if (!this._nativeMap) {
            return; // map not ready yet — ngAfterViewInit's mapReady will call this again
        }
        // Drop the previous markers + bubbles and their listeners.
        this._rentalSalesMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        this._rentalSalesMarkers = this.mapRentalSales.map(function (rs) {
            var marker = new google.maps.Marker({
                position: { lat: rs.location.lat, lng: rs.location.lon },
                icon: _this.getRentalSalesIconUrl(rs),
                opacity: 0.8,
                title: rs.name
            });
            marker.addListener('click', function () { return _this._zone.run(function () { return _this.onRentalSalesMarkerClick(rs); }); });
            marker.addListener('mouseover', function () { return _this._zone.run(function () { return _this.onRentalSalesMarkerHover(rs, true); }); });
            marker.addListener('mouseout', function () { return _this._zone.run(function () { return _this.onRentalSalesMarkerHover(rs, false); }); });
            return marker;
        });
        this.recluster();
    };
    ResultsMapComponent.prototype.ngOnDestroy = function () {
        clearTimeout(this._reclusterTimer);
        this._clubMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._schoolMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._universityMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._fboMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._serviceRepairMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._rentalSalesMarkers.forEach(function (m) { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clubMarkers = [];
        this._schoolMarkers = [];
        this._universityMarkers = [];
        this._fboMarkers = [];
        this._serviceRepairMarkers = [];
        this._rentalSalesMarkers = [];
        this._clusterBubbles = [];
    };
    /** Keeps the component's zoom in sync with the map, and drives the re-cluster (raw 'idle' is unreliable here). */
    ResultsMapComponent.prototype.onZoomChange = function (newZoomValue) {
        this.currZoom = newZoomValue;
        this._lastClusterZoom = newZoomValue;
        this.scheduleRecluster();
    };
    /** Debounced re-cluster (coalesces rapid zoom settles). */
    ResultsMapComponent.prototype.scheduleRecluster = function () {
        var _this = this;
        if (!this._nativeMap || !this._finderMarkers.length) {
            return;
        }
        clearTimeout(this._reclusterTimer);
        this._reclusterTimer = setTimeout(function () { return _this.recluster(); }, 120);
    };
    /**
     * Self-managed grid clustering. Groups club markers within CLUSTER_GRID_PX screen pixels (at the current
     * zoom) into one count bubble and shows the rest individually. At/above DECLUSTER_ZOOM (or before the map
     * projection is ready) every club shows individually. Bubbles are plain google.maps.Markers, so setMap(null)
     * reliably removes them — no orphaned overlay DOM (the whole reason the js-marker-clusterer was dropped).
     */
    ResultsMapComponent.prototype.recluster = function () {
        var markers = this._finderMarkers;
        if (!this._nativeMap || !markers.length) {
            return;
        }
        // Remove the previous bubbles (reliable — plain markers).
        this._clusterBubbles.forEach(function (b) { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
        this._clusterBubbles = [];
        var map = this._nativeMap;
        var zoom = map.getZoom();
        var projection = map.getProjection();
        // No clustering: show every marker individually.
        if (!projection || zoom >= this.DECLUSTER_ZOOM) {
            markers.forEach(function (m) { if (m.getMap() !== map) {
                m.setMap(map);
            } });
            return;
        }
        // Project each marker to screen pixels (world point * 2^zoom), then greedily group by pixel proximity.
        var scale = Math.pow(2, zoom);
        var pts = markers.map(function (m) {
            var wp = projection.fromLatLngToPoint(m.getPosition());
            return { x: wp.x * scale, y: wp.y * scale };
        });
        var used = markers.map(function () { return false; });
        var _loop_1 = function (i) {
            if (used[i]) {
                return "continue";
            }
            var group = [i];
            used[i] = true;
            for (var j = i + 1; j < markers.length; j++) {
                if (used[j]) {
                    continue;
                }
                var dx = pts[i].x - pts[j].x;
                var dy = pts[i].y - pts[j].y;
                if (Math.sqrt(dx * dx + dy * dy) <= this_1.CLUSTER_GRID_PX) {
                    group.push(j);
                    used[j] = true;
                }
            }
            if (group.length === 1) {
                var marker = markers[group[0]];
                if (marker.getMap() !== map) {
                    marker.setMap(map);
                }
            }
            else {
                var lat_1 = 0, lng_1 = 0;
                group.forEach(function (k) {
                    var pos = markers[k].getPosition();
                    lat_1 += pos.lat();
                    lng_1 += pos.lng();
                    markers[k].setMap(null); // hide members behind the bubble
                });
                this_1._clusterBubbles.push(this_1.makeClusterBubble({ lat: lat_1 / group.length, lng: lng_1 / group.length }, group.length));
            }
        };
        var this_1 = this;
        for (var i = 0; i < markers.length; i++) {
            _loop_1(i);
        }
    };
    /** Builds a plain-marker count bubble (blue circle icon + white count label). */
    ResultsMapComponent.prototype.makeClusterBubble = function (position, count) {
        var _this = this;
        var size = count < 10 ? 38 : (count < 100 ? 46 : 54);
        var bubble = new google.maps.Marker({
            position: position,
            map: this._nativeMap,
            icon: {
                url: blueClusterBubble(size),
                scaledSize: new google.maps.Size(size, size),
                anchor: new google.maps.Point(size / 2, size / 2),
                labelOrigin: new google.maps.Point(size / 2, size / 2)
            },
            label: { text: String(count), color: '#ffffff', fontWeight: 'bold', fontSize: count < 100 ? '13px' : '11px' },
            zIndex: 10000
        });
        bubble.addListener('click', function () { return _this._zone.run(function () { return _this.onClusterBubbleClick(position); }); });
        return bubble;
    };
    /** Zoom toward a cluster when its bubble is clicked. */
    ResultsMapComponent.prototype.onClusterBubbleClick = function (position) {
        var map = this._nativeMap;
        map.panTo(position);
        map.setZoom(Math.min(map.getZoom() + 3, this.DECLUSTER_ZOOM));
    };
    ResultsMapComponent.prototype.updateSize = function () {
        this.map.triggerResize(true);
    };
    ResultsMapComponent.prototype.setCenter = function (center) {
        var latlon = { lat: center.lat, lng: center.lon };
        this._nativeMap.setCenter(latlon);
    };
    ResultsMapComponent.prototype.updateBounds = function () {
        var _this = this;
        setTimeout(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var bounds, offset, center, offset;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._google.promise];
                    case 1:
                        _a.sent();
                        if ((this.airports || []).length > 0 || (this.activities || []).length || (this.flyingClubs || []).length
                            || (this.flightSchools || []).length || (this.universities || []).length
                            || (this.fbos || []).length || (this.serviceRepairs || []).length
                            || (this.rentalSales || []).length) { //  || (this.aviationEvents || []).length
                            // Fit airport markers
                            bounds = new google.maps.LatLngBounds();
                            (this.airports || []).forEach(function (airport) {
                                bounds.extend(new google.maps.LatLng(airport.location.lat, airport.location.lon));
                            });
                            // Fit activity markers
                            (this.activities || []).forEach(function (activity) {
                                bounds.extend(activity.geometry.location);
                            });
                            // Fit flying club markers
                            (this.flyingClubs || []).forEach(function (flyingClub) {
                                if (flyingClub.location && flyingClub.location.lat && flyingClub.location.lon) {
                                    bounds.extend(new google.maps.LatLng(flyingClub.location.lat, flyingClub.location.lon));
                                }
                            });
                            // Fit flight school markers
                            (this.flightSchools || []).forEach(function (flightSchool) {
                                if (flightSchool.location && flightSchool.location.lat && flightSchool.location.lon) {
                                    bounds.extend(new google.maps.LatLng(flightSchool.location.lat, flightSchool.location.lon));
                                }
                            });
                            // Fit university markers
                            (this.universities || []).forEach(function (university) {
                                if (university.location && university.location.lat && university.location.lon) {
                                    bounds.extend(new google.maps.LatLng(university.location.lat, university.location.lon));
                                }
                            });
                            // Fit FBO markers
                            (this.fbos || []).forEach(function (fbo) {
                                if (fbo.location && fbo.location.lat && fbo.location.lon) {
                                    bounds.extend(new google.maps.LatLng(fbo.location.lat, fbo.location.lon));
                                }
                            });
                            // Fit service/repair markers
                            (this.serviceRepairs || []).forEach(function (sr) {
                                if (sr.location && sr.location.lat && sr.location.lon) {
                                    bounds.extend(new google.maps.LatLng(sr.location.lat, sr.location.lon));
                                }
                            });
                            // Fit rentals/sales markers
                            (this.rentalSales || []).forEach(function (rs) {
                                if (rs.location && rs.location.lat && rs.location.lon) {
                                    bounds.extend(new google.maps.LatLng(rs.location.lat, rs.location.lon));
                                }
                            });
                            offset = 0.008;
                            center = bounds.getCenter();
                            bounds.extend(new google.maps.LatLng(center.lat() + offset, center.lng() + offset));
                            bounds.extend(new google.maps.LatLng(center.lat() - offset, center.lng() - offset));
                        }
                        else if (this.initialBounds) {
                            bounds = {
                                north: this.initialBounds.ne.lat,
                                east: this.initialBounds.ne.lon,
                                south: this.initialBounds.sw.lat,
                                west: this.initialBounds.sw.lon
                            };
                        }
                        else if (this.initialLocation) {
                            bounds = new google.maps.LatLngBounds();
                            offset = 2.5;
                            bounds.extend(new google.maps.LatLng(this.initialLocation.lat + offset, this.initialLocation.lon + offset));
                            bounds.extend(new google.maps.LatLng(this.initialLocation.lat - offset, this.initialLocation.lon - offset));
                            this.setCenter(this.initialLocation);
                        }
                        else {
                            // Center on CONUS
                            bounds = new google.maps.LatLngBounds(new google.maps.LatLng(24.396308, -124.848974), new google.maps.LatLng(49.384358, -66.885444));
                        }
                        this._nativeMap.fitBounds(bounds);
                        return [2 /*return*/];
                }
            });
        }); }, 0);
    };
    ResultsMapComponent.prototype.getIconUrl = function (activity) {
        return GoogleService.getIconUrl(activity);
    };
    ResultsMapComponent.prototype.getAirportIconUrl = function (airport) {
        if (!airport.minPrice || !airport.minPriceType) {
            return 'assets/icons/airport-map-pin.png';
        }
        if (airport.minPriceType === 'High') {
            return 'assets/icons/square-pin-red.png';
        }
        else if (airport.minPriceType === 'Medium') {
            return 'assets/icons/square-pin-yellow.png';
        }
        else {
            return 'assets/icons/square-pin-green.png';
        }
    };
    ResultsMapComponent.prototype.getAviationEventIconUrl = function (aviationEvent) {
        if (aviationEvent.type === AviationEventType.RustyPilotsSeminar) {
            return 'assets/icons/Rusty-Pilots-Event-Map-Marker.png';
        }
        else {
            return 'assets/icons/Event-Map-Marker.png';
        }
    };
    ResultsMapComponent.prototype.getEventIconZIndex = function (aviationEvent) {
        if (aviationEvent.type === AviationEventType.RustyPilotsSeminar) {
            return 600;
        }
        else {
            return 500;
        }
    };
    ResultsMapComponent.prototype.getFlyingClubIconUrl = function (flyingClub) {
        // Marker icon by club type, matching FlyingclubSummaryComponent.iconUrl.
        if (flyingClub.clubType === 'AOPA Network Club') {
            return 'assets/icons/networkicon.png';
        }
        if (flyingClub.clubType === 'Club In Formation') {
            return 'assets/icons/informationicon.png';
        }
        return 'assets/icons/othericon.png';
    };
    ResultsMapComponent.prototype.getFlightSchoolIconUrl = function (flightSchool) {
        // All flight schools share the inline blue graduation-cap pin (distinct from the flying-club PNG pins).
        return FLIGHT_SCHOOL_MARKER_ICON;
    };
    ResultsMapComponent.prototype.getUniversityIconUrl = function (university) {
        // All universities share the inline purple building pin (distinct from the flight-school pins).
        return UNIVERSITY_MARKER_ICON;
    };
    ResultsMapComponent.prototype.getFboIconUrl = function (fbo) {
        // All FBOs share the inline red fuel-pump pin (distinct from the other finders' pins).
        return FBO_MARKER_ICON;
    };
    ResultsMapComponent.prototype.getServiceRepairIconUrl = function (serviceRepair) {
        // All service/repair businesses share the inline green wrench pin (distinct from the other finders' pins).
        return SERVICE_REPAIR_MARKER_ICON;
    };
    ResultsMapComponent.prototype.getRentalSalesIconUrl = function (rentalSales) {
        // All rentals/sales businesses share the inline green price-tag pin (distinct from the other finders' pins).
        return RENTAL_SALES_MARKER_ICON;
    };
    ResultsMapComponent.prototype.onAirportMarkerHover = function (airport, hover) {
        this.toggleAirportInfoTooltip(airport, hover);
        if (hover) {
            this.airportMouseEnter.emit(airport);
        }
        else {
            this.airportMouseLeave.emit(airport);
        }
    };
    ResultsMapComponent.prototype.onAirportMarkerClick = function (airport) {
        this.airportClick.emit(airport);
    };
    ResultsMapComponent.prototype.onActivityMarkerHover = function (activity, hover) {
        var _this = this;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.activityMouseEnter.emit(activity);
            }
            else {
                _this.activityMouseLeave.emit(activity);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onActivityMarkerClick = function (activity) {
        this.activityClick.emit(activity);
    };
    ResultsMapComponent.prototype.onAviationEventMarkerHover = function (aviationEvent, hover) {
        var _this = this;
        this.toggleAviationEventInfoTooltip(aviationEvent, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.aviationEventMouseEnter.emit(aviationEvent);
            }
            else {
                _this.aviationEventMouseLeave.emit(aviationEvent);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onAviationEventMarkerClick = function (aviationEvent) {
        this.aviationEventClick.emit(aviationEvent);
    };
    ResultsMapComponent.prototype.onFlyingClubMarkerHover = function (flyingClub, hover) {
        var _this = this;
        this.toggleFlyingClubInfoTooltip(flyingClub, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.flyingClubMouseEnter.emit(flyingClub);
            }
            else {
                _this.flyingClubMouseLeave.emit(flyingClub);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onFlyingClubMarkerClick = function (flyingClub) {
        this.flyingClubClick.emit(flyingClub);
    };
    ResultsMapComponent.prototype.onFlightSchoolMarkerHover = function (flightSchool, hover) {
        var _this = this;
        this.toggleFlightSchoolInfoTooltip(flightSchool, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.flightSchoolMouseEnter.emit(flightSchool);
            }
            else {
                _this.flightSchoolMouseLeave.emit(flightSchool);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onFlightSchoolMarkerClick = function (flightSchool) {
        this.flightSchoolClick.emit(flightSchool);
    };
    ResultsMapComponent.prototype.onUniversityMarkerHover = function (university, hover) {
        var _this = this;
        this.toggleUniversityInfoTooltip(university, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.universityMouseEnter.emit(university);
            }
            else {
                _this.universityMouseLeave.emit(university);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onUniversityMarkerClick = function (university) {
        this.universityClick.emit(university);
    };
    ResultsMapComponent.prototype.onFboMarkerHover = function (fbo, hover) {
        var _this = this;
        this.toggleFboInfoTooltip(fbo, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.fboMouseEnter.emit(fbo);
            }
            else {
                _this.fboMouseLeave.emit(fbo);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onFboMarkerClick = function (fbo) {
        this.fboClick.emit(fbo);
    };
    ResultsMapComponent.prototype.onServiceRepairMarkerHover = function (serviceRepair, hover) {
        var _this = this;
        this.toggleServiceRepairInfoTooltip(serviceRepair, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.serviceRepairMouseEnter.emit(serviceRepair);
            }
            else {
                _this.serviceRepairMouseLeave.emit(serviceRepair);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onServiceRepairMarkerClick = function (serviceRepair) {
        this.serviceRepairClick.emit(serviceRepair);
    };
    ResultsMapComponent.prototype.onRentalSalesMarkerHover = function (rentalSales, hover) {
        var _this = this;
        this.toggleRentalSalesInfoTooltip(rentalSales, hover);
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(function () {
            if (hover) {
                _this.rentalSalesMouseEnter.emit(rentalSales);
            }
            else {
                _this.rentalSalesMouseLeave.emit(rentalSales);
            }
        }, 400);
    };
    ResultsMapComponent.prototype.onRentalSalesMarkerClick = function (rentalSales) {
        this.rentalSalesClick.emit(rentalSales);
    };
    ResultsMapComponent.prototype.toggleAirportInfoTooltip = function (airport, open) {
        // Don't show tooltip window if the full size card is opened
        open = open && (this.selectedAirport !== airport);
        this.toggleAirportInfoWindow(airport, this.airportInfoTooltip, open);
    };
    ResultsMapComponent.prototype.toggleAirportInfoWindow = function (airport, window, open) {
        var i = this.airports.indexOf(airport);
        if (i < 0) {
            return;
        }
        var marker = this.airportMarkers.toArray()[i];
        if (open) {
            this.infoWindowOpen(window, marker);
        }
        else {
            this.infoWindowClose(window);
        }
    };
    ResultsMapComponent.prototype.toggleAviationEventInfoTooltip = function (aviationEvent, open) {
        // Don't show tooltip window if the full size card is opened
        open = open && (this.selectedAviationEvent !== aviationEvent);
        this.toggleAviationEventInfoWindow(aviationEvent, this.aviationEventInfoTooltip, open);
    };
    ResultsMapComponent.prototype.toggleAviationEventInfoWindow = function (aviationEvent, window, open) {
        var i = this.aviationEvents.indexOf(aviationEvent);
        if (i < 0) {
            return;
        }
        var marker = this.aviationEventMarkers.toArray()[i];
        if (open) {
            this.infoWindowOpen(window, marker);
        }
        else {
            this.infoWindowClose(window);
        }
    };
    ResultsMapComponent.prototype.toggleFlyingClubInfoTooltip = function (flyingClub, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._clubMarkers[this.mapFlyingClubs.indexOf(flyingClub)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = flyingClub.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    ResultsMapComponent.prototype.toggleFlightSchoolInfoTooltip = function (flightSchool, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._schoolMarkers[this.mapFlightSchools.indexOf(flightSchool)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = flightSchool.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    ResultsMapComponent.prototype.toggleUniversityInfoTooltip = function (university, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._universityMarkers[this.mapUniversities.indexOf(university)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = university.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    ResultsMapComponent.prototype.toggleFboInfoTooltip = function (fbo, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._fboMarkers[this.mapFbos.indexOf(fbo)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = fbo.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    ResultsMapComponent.prototype.toggleServiceRepairInfoTooltip = function (serviceRepair, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._serviceRepairMarkers[this.mapServiceRepairs.indexOf(serviceRepair)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = serviceRepair.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    ResultsMapComponent.prototype.toggleRentalSalesInfoTooltip = function (rentalSales, open) {
        var _this = this;
        if (!this._finderInfoWindow || !this._nativeMap) {
            return;
        }
        if (open) {
            var marker = this._rentalSalesMarkers[this.mapRentalSales.indexOf(rentalSales)];
            if (!marker) {
                return;
            }
            var content = document.createElement('div');
            content.className = 'results-map__airport-tooltip';
            content.textContent = rentalSales.name;
            this._finderInfoWindow.setContent(content);
            this._openFinderInfoWindow(marker);
        }
        else {
            this._zone.runOutsideAngular(function () { return _this._finderInfoWindow.close(); });
        }
    };
    /**
     * Opens the shared finder tooltip (content already set) for a marker. When the marker is visible it anchors
     * to it, so the bubble floats above the pin. When the marker is hidden inside a cluster it opens by explicit
     * position instead — otherwise Google Maps ignores the off-map anchor and leaves the window on the
     * previously hovered (visible) marker, making the tooltip appear over a random pin.
     */
    ResultsMapComponent.prototype._openFinderInfoWindow = function (marker) {
        var _this = this;
        this._zone.runOutsideAngular(function () {
            if (marker.getMap()) {
                _this._finderInfoWindow.open(_this._nativeMap, marker);
            }
            else {
                _this._finderInfoWindow.close();
                _this._finderInfoWindow.setPosition(marker.getPosition());
                _this._finderInfoWindow.open(_this._nativeMap);
            }
        });
    };
    ResultsMapComponent.prototype.infoWindowOpen = function (infoWindow, marker) {
        if (marker === void 0) { marker = null; }
        infoWindow.hostMarker = marker;
        // Run outside angular to avoid triggering an endless cycle of change detection:
        // https://github.com/SebastianM/angular-google-maps/issues/796
        this._zone.runOutsideAngular(function () {
            infoWindow.open();
        });
    };
    ResultsMapComponent.prototype.infoWindowClose = function (infoWindow) {
        this._zone.runOutsideAngular(function () {
            infoWindow.close();
        });
    };
    ResultsMapComponent.prototype.onBoundsChange = function (bounds) {
        var ne = LatLon.fromAgm(bounds.getNorthEast());
        var sw = LatLon.fromAgm(bounds.getSouthWest());
        var box = new BoundingBox(ne, sw);
        this.boundsChange.emit(box);
    };
    ResultsMapComponent.prototype.onCloseAirportCardWindow = function () {
        this.airportCardClose.emit();
    };
    ResultsMapComponent.prototype.onCloseActivityWindow = function (activity) {
        this.activityCardClose.emit(activity);
    };
    ResultsMapComponent.prototype.onCloseAviationEventCardWindow = function () {
        this.aviationEventCardClose.emit();
    };
    return ResultsMapComponent;
}());
export { ResultsMapComponent };
